import { useEffect } from 'react';
import TableContainer from '@/components/_common/Table/TableContainer';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { applyFilters, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { useQuery } from '@/components/_hooks/useQuery';
import styled from '@emotion/styled';
import { selectActiveTitleId, selectTitleIsSeries, selectTitleLoading } from '@/redux/selectors/titleSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectOutputVersionOptions } from '@/redux/selectors/orderPlacementSelectors';
import Spinner from '@/components/_common/Spinner';
import { Grid } from '@mui/material';

interface TitleDetailRelatedJobsProps {
  lookupId: string;
}

const TitleDetailRelatedJobs = ({ lookupId }: TitleDetailRelatedJobsProps) => {
  const dispatch = useAppDispatch();
  const currentTable = useAppSelector(selectCurrentTableKey);
  const query = useQuery();
  const versionId = query.get('version_id');
  const isSeries = useAppSelector(selectTitleIsSeries);
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const outputVersionOptions = useAppSelector(selectOutputVersionOptions);
  const versions = outputVersionOptions[lookupId] || [];
  const titleLoading = useAppSelector(selectTitleLoading);

  useEffect(() => {
    if (titleLoading) return;
    dispatch(setCurrentTable('titleDetailJobs'));
    const filters: Record<string, string[] | undefined> = {
      title_hid_filter: undefined,
      version_hid_filter: undefined
    };

    if (isSeries || !versionId || versionId === 'all') {
      filters.title_hid_filter = [`E${activeTitleId}`];
    } else {
      const version = versions.find((version) => String(version.id) === versionId);
      filters.version_hid_filter = version?.hid ? [version.hid] : undefined;
    }

    dispatch(clearFilters(undefined));
    dispatch(applyFilters({ filters }));
  }, [versionId, activeTitleId, titleLoading]);

  return !titleLoading && currentTable === 'titleDetailJobs' ? (
    <StyledTableContainer
      onRowClick={(_, job) => {
        window.open(`/jobs/${job.id}`, '_blank');
      }}
      simplified
    />
  ) : (
    <Grid item sx={{ p: 6 }}>
      <Spinner contained />
    </Grid>
  );
};

export default TitleDetailRelatedJobs;

const StyledTableContainer = styled(TableContainer)`
  .MuiTableHead-root {
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  #overflow {
    background: ${({ theme }) => theme.palette.background.paper};

    > span:nth-of-type(2) {
      background: ${({ theme }) => `linear-gradient(to left, ${theme.palette.background.paper}, transparent 75%)`};
    }
  }
`;
